import {
	Button,
	Checkbox,
	Elem,
	ElemBefore,
	IconButton,
	SearchSelect,
	TextInput,
} from "@alm/flow-ds/components";
import { filterValues, operatorValues } from "../../../../constants";
import { useCreateOptions } from "hooks/FormDesigner/useCreateOptions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonPropertyProps, FilterValue, Payload } from "schemas/elements";
import { TextValidation } from "validation/validation";
import { booleanOptions, operatorOptions } from "../options";

type Props = Omit<CommonPropertyProps, "value"> & {
	value: Payload;
	nextProp?: string;
};

export default ({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	nextProp,
	id,
	nestedField,
}: Props) => {
	const { t } = useTranslation();

	// When a new filter is added, these are the defaults
	const newItem = useMemo(() => {
		return {
			filter: filterValues.AND,
			field: undefined,
			operator: operatorValues.CONTAINS,
			value: {},
			parentFilter: filterValues.AND,
		};
	}, []);

	const [init, setInit] = useState(false);

	// Create SearchSelect options
	const operatorOps = useCreateOptions(operatorOptions);
	const booleanOps = useCreateOptions(booleanOptions);
	const [values, setValues] = useState(value.filter ? value.filter : []);

	/**
	 * Handles filter/parentFilter changes
	 *
	 * @param e event object
	 * @param index index of item changed
	 */
	const handleUpdateCheckbox = useCallback(
		(e, index) => {
			const newVal =
				e.currentTarget.value === filterValues.NOT
					? filterValues.AND
					: filterValues.NOT;
			const currentValues = [...values];
			// Check if the filter is OR or not to determine whether
			// we update the parentFilter or filter
			currentValues[index] = {
				...currentValues[index],
				...(currentValues[index].filter !== filterValues.OR
					? { filter: newVal }
					: {}),
				...(currentValues[index].filter === filterValues.OR
					? { parentFilter: newVal }
					: {}),
			};
			setValues(currentValues);
			handleUpdateStore(currentValues);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[values]
	);

	/**
	 * Handles SearchSelect and TextInput changes
	 *
	 * @param e event object
	 * @param index index of item changed
	 * @param property property to update
	 * @param val value to change to
	 */
	const handleChange = useCallback(
		(e, index, property, val?) => {
			const newVal = e.currentTarget ? e.currentTarget.value : e.value;
			const currentValues = [...values];
			currentValues[index] = {
				...currentValues[index],
				...(property === "operator" &&
				currentValues[index].operator !== newVal &&
				newVal === operatorValues.ONE_OF
					? {
							value: [newItem],
							parentFilter: currentValues[index].filter,
							filter: filterValues.OR,
					  }
					: property === "operator" &&
					  currentValues[index].operator !== newVal
					? {
							value: {},
							filter:
								currentValues[index].filter === filterValues.OR
									? currentValues[index].parentFilter
									: currentValues[index].filter,
					  }
					: {}),
				[property]: val
					? {
							...currentValues[index][property],
							[val]: newVal,
					  }
					: newVal,
			};
			setValues(currentValues);
		},
		[newItem, values]
	);

	/**
	 * Handles changes to oneOf inputs (OR inputs)
	 *
	 * @param e event object
	 * @param index index of item changed
	 * @param nestedIndex index in value array of the OR item
	 */
	const handleNestedChange = useCallback(
		(e, index, nestedIndex) => {
			const newVal = e.currentTarget.value;
			const currentValues = [...values];
			const nestedCurrentValues = [...currentValues[index].value];
			nestedCurrentValues[nestedIndex] = {
				...nestedCurrentValues[nestedIndex],
				value: { value: newVal },
			};
			currentValues[index] = {
				...currentValues[index],
				value: nestedCurrentValues,
			};
			setValues(currentValues);
		},
		[values]
	);

	// Updating store function for all inputs
	const handleUpdateStore = useCallback(
		(vals = values) => {
			updateStore({
				elementIndex,
				property: field,
				nestedField,
				value: nextProp
					? {
							...value,
							[nextProp]: vals,
					  }
					: vals,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[elementIndex, field, nextProp, updateStore, value, values]
	);

	// Handles adding new filters
	const handleAdd = useCallback(() => {
		setValues([...values, newItem]);
	}, [newItem, values]);

	// Handles removing filters
	const handleRemove = useCallback(
		index => {
			const currentValues = [...values];
			currentValues.splice(index, 1);
			setValues(currentValues);
		},
		[values]
	);

	// Handles adding new OR filters in the same filter
	const handleNestedAdd = useCallback(
		index => {
			const currentValues = [...values];
			const currentItemValues = { ...currentValues[index] };
			currentItemValues.value = [...currentItemValues.value, newItem];
			currentValues[index] = {
				...currentItemValues,
			};
			setValues([...currentValues]);
		},
		[newItem, values]
	);

	// Handles removing OR filters in the same filter
	const handleNestedRemove = useCallback(
		(index, nestedIndex) => {
			const currentValues = [...values];
			let currentItemValues = { ...currentValues[index] };
			const currentNestedValues = [...currentItemValues.value];
			currentNestedValues.splice(nestedIndex, 1);
			currentItemValues = {
				...currentItemValues,
				value: currentNestedValues,
			};
			currentValues[index] = {
				...currentItemValues,
			};
			setValues([...currentValues]);
		},
		[values]
	);

	useEffect(() => {
		if (init) handleUpdateStore();
		else setInit(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.length]);

	return (
		<Elem className="elem-filter">
			<ElemBefore
				label={
					nextProp
						? t(`formDesigner.properties.${nextProp}`)
						: t(`formDesigner.properties.${field}`)
				}
				required={required}
				id={id}
			/>
			<div className="hint-text">
				{t(`formDesigner.elements.${desc}`)}
			</div>
			<div className={`elem-filter-properties`}>
				{values?.map(
					(
						{
							filter,
							field,
							operator,
							value,
							parentFilter,
						}: FilterValue,
						index: number
					) => {
						return (
							<div className="elem-filter-item" key={index}>
								<TextInput
									id={`${id}-${index}-field`}
									label={t("formDesigner.labels.field")}
									onChange={e =>
										handleChange(e, index, "field")
									}
									value={field}
									labelHidden={true}
									placeholder={t("formDesigner.labels.field")}
									onBlur={() => handleUpdateStore()}
									status={
										validation && !TextValidation(field)
											? "error"
											: undefined
									}
								/>
								{/* When ticked the operator is NOT */}
								<Checkbox
									id={`fiilter-${index}`}
									className="filter-checkbox"
									value={
										filter === filterValues.OR &&
										parentFilter
											? parentFilter
											: filter
									}
									checked={
										filter === filterValues.OR
											? parentFilter === filterValues.NOT
											: filter === filterValues.NOT
									}
									label={t("formDesigner.labels.not")}
									onChange={e =>
										handleUpdateCheckbox(e, index)
									}
								/>
								<SearchSelect
									id={`${id}-${index}-op`}
									onChange={e =>
										handleChange(e, index, "operator")
									}
									key={`operator-${index}`}
									label={t("formDesigner.labels.operator")}
									labelHidden={true}
									value={
										operator
											? {
													value: operator,
													label: t(
														`lists.operator.${operator}`
													),
											  }
											: undefined
									}
									placeholder={t(
										"formDesigner.labels.operator"
									)}
									options={operatorOps}
									onBlur={() => handleUpdateStore()}
								/>
								{operator !== operatorValues.EXISTS &&
									operator !== operatorValues.ONE_OF && (
										<TextInput
											id={`${id}-${index}-value`}
											label={t(
												"formDesigner.labels.value"
											)}
											onChange={e =>
												handleChange(
													e,
													index,
													"value",
													"value"
												)
											}
											value={value.value}
											labelHidden={true}
											placeholder={t(
												"formDesigner.labels.value"
											)}
											onBlur={() => handleUpdateStore()}
											status={
												validation &&
												!TextValidation(value.value)
													? "error"
													: undefined
											}
										/>
									)}
								{operator === operatorValues.EXISTS && (
									<SearchSelect
										id={`${id}-${index}-value`}
										onChange={e =>
											handleChange(
												e,
												index,
												"value",
												"value"
											)
										}
										key={`exists-${index}`}
										label={t("formDesigner.labels.value")}
										labelHidden={true}
										value={
											value.value !== undefined
												? {
														value: value.value,
														label: t(
															`lists.boolean.${value.value}`
														),
												  }
												: undefined
										}
										placeholder={t(
											"formDesigner.labels.value"
										)}
										options={booleanOps}
										onBlur={() => handleUpdateStore()}
										className={
											validation && !value.value
												? " invalid"
												: ""
										}
									/>
								)}
								{operator !== operatorValues.EXISTS &&
									operator !== operatorValues.CONTAINS &&
									operator !== operatorValues.EXACT &&
									operator !== operatorValues.ONE_OF && (
										<TextInput
											id={`${id}-${index}-format`}
											label={t(
												"formDesigner.labels.format"
											)}
											onChange={e =>
												handleChange(
													e,
													index,
													"value",
													"format"
												)
											}
											value={value.format}
											labelHidden={true}
											placeholder={t(
												"formDesigner.labels.format"
											)}
											onBlur={() => handleUpdateStore()}
										/>
									)}
								{operator === operatorValues.EXACT && (
									<TextInput
										id={`${id}-${index}-mode`}
										label={t("formDesigner.labels.mode")}
										onChange={e =>
											handleChange(
												e,
												index,
												"value",
												"mode"
											)
										}
										value={value.mode}
										labelHidden={true}
										placeholder={t(
											"formDesigner.labels.mode"
										)}
										onBlur={() => handleUpdateStore()}
									/>
								)}
								{operator === operatorValues.ONE_OF && (
									<div className="elem">
										{value.map(
											(
												item: FilterValue,
												nestedIndex: number
											) => {
												return (
													<TextInput
														key={nestedIndex}
														className="elem-nested-filter"
														id={`${id}-${index}-${nestedIndex}-value`}
														label={t(
															"formDesigner.labels.value"
														)}
														onChange={e =>
															handleNestedChange(
																e,
																index,
																nestedIndex
															)
														}
														value={item.value.value}
														labelHidden={true}
														placeholder={t(
															"formDesigner.labels.value"
														)}
														onBlur={() =>
															handleUpdateStore()
														}
														status={
															validation &&
															!TextValidation(
																item.value.value
															)
																? "error"
																: undefined
														}
														after={
															<div className="input-group-append">
																<IconButton
																	onClick={() =>
																		handleNestedRemove(
																			index,
																			nestedIndex
																		)
																	}
																	icon="times"
																	className="btn-outline-light mb-0"
																	label={t(
																		"formDesigner.labels.remove"
																	)}
																	disabled={
																		value.length ===
																		1
																	}
																/>
															</div>
														}
													/>
												);
											}
										)}
										<Button
											className="btn-link p-0 mb-0"
											label={t(
												"formDesigner.labels.addValue"
											)}
											onClick={() =>
												handleNestedAdd(index)
											}
										/>
									</div>
								)}
								<IconButton
									label={t("formDesigner.labels.remove")}
									icon="times"
									onClick={() => handleRemove(index)}
									className="btn-outline-danger"
								/>
							</div>
						);
					}
				)}
				<Button
					label={t("formDesigner.labels.add")}
					onClick={handleAdd}
					className="btn-default"
				/>
			</div>
		</Elem>
	);
};
